/**
 * Camera-based barcode/QR scanner overlay for POS billing.
 *
 * Uses html5-qrcode (ZXing-based) to decode barcodes from the device camera.
 * Supports: EAN-13, EAN-8, UPC-A, UPC-E, Code 128, Code 39, QR Code.
 * Works offline (camera scanning is client-side only).
 *
 * RESPONSIVE_DESIGN.md §2.1.7:
 *   xs-sm: 280px viewfinder, torch + close
 *   md:    320px viewfinder
 *   lg+:   360px viewfinder, recent scans sidebar (200px)
 *
 * DESIGN_TOKENS.md: z-scanner = 80 (above modals z-50, below critical alerts)
 *
 * Traceability: FR-PROD-006, FR-PROD-013, FR-PROD-023, US-INV-005, US-INV-013
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@bbpos/ui';
import { captureFrameFromVideo } from '@/lib/camera-capture';
import { productsService, type ImageLookupCandidate } from '@/services/products.service';
import { ImageMatchCandidatePicker } from './ImageMatchCandidatePicker';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CameraBarcodeScannerProps {
  open: boolean;
  onScan: (barcode: string, format: string) => void;
  onClose: () => void;
  soundEnabled?: boolean;
  /** Preferred camera deviceId (from settings) */
  preferredCameraId?: string;
  /** Keep scanner open after each scan for rapid multi-item scanning */
  continuousMode?: boolean;
  /**
   * Optional image-based product lookup. When provided, a "Match by image"
   * button is rendered alongside the torch toggle. Tapping it captures a
   * still frame, sends it to /products/lookup/by-image, and lets the cashier
   * pick the best match from the returned candidates.
   */
  onImageMatch?: (product: ImageLookupCandidate['product']) => void;
  /**
   * Optional escape hatch shown in the image-match empty state. When the
   * picker returns zero candidates and the caller supplies this handler
   * AND the user is MANAGER+, a primary "Add as new product" CTA is
   * rendered (e.g. navigate to Quick-Add pre-scoped to this location).
   */
  onImageMatchAddAsNew?: () => void;
  /** Optional location id passed through to the lookup endpoint. */
  locationId?: string;
}

type ScanState = 'initializing' | 'scanning' | 'found' | 'not-found' | 'error';

interface RecentScan {
  barcode: string;
  format: string;
  timestamp: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const SCANNER_ELEMENT_ID = 'bbpos-camera-scanner';

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// Dedup window: ignore same barcode scanned within this interval
const DEDUP_INTERVAL_MS = 2000;

// ─── Beep via Web Audio API (no audio file dependency) ──────────────────────

let audioCtx: AudioContext | null = null;

function playBeep(frequency = 1200, duration = 120) {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.value = frequency;
    osc.type = 'square';
    gain.gain.value = 0.15;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration / 1000);
    osc.stop(audioCtx.currentTime + duration / 1000);
  } catch {
    // Audio not available — silently ignore
  }
}

/** Haptic feedback for mobile devices — standard POS scanner UX */
function vibrate() {
  try {
    navigator.vibrate?.(100);
  } catch {
    // Vibration API not available
  }
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function FlashlightIcon({ on }: { on: boolean }) {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 019 14" />
    </svg>
  );
}

function CameraSparkleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h13" />
      <circle cx="11" cy="13" r="3" />
      <path d="M19 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
    </svg>
  );
}

function CameraOffIcon() {
  return (
    <svg className="h-16 w-16 text-neutral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 1l22 22M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3l2-3h6l2 3h3a2 2 0 012 2v9.34" />
      <path d="M14.12 14.12A3 3 0 109.88 9.88" />
    </svg>
  );
}

// ─── Viewfinder Corner Brackets ─────────────────────────────────────────────
// Renders at the correct size per breakpoint using CSS classes

function ViewfinderCorners() {
  const L = 24; // bracket arm length
  const T = 3;  // bracket thickness
  const style = { background: 'white', borderRadius: 2 };
  return (
    <div className="absolute inset-0 pointer-events-none w-[280px] h-[280px] md:w-[320px] md:h-[320px] lg:w-[360px] lg:h-[360px]">
      {/* Top-left */}
      <div className="absolute top-0 left-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute top-0 left-0" style={{ width: T, height: L, ...style }} />
      {/* Top-right */}
      <div className="absolute top-0 right-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute top-0 right-0" style={{ width: T, height: L, ...style }} />
      {/* Bottom-left */}
      <div className="absolute bottom-0 left-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute bottom-0 left-0" style={{ width: T, height: L, ...style }} />
      {/* Bottom-right */}
      <div className="absolute bottom-0 right-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute bottom-0 right-0" style={{ width: T, height: L, ...style }} />
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CameraBarcodeScanner({
  open,
  onScan,
  onClose,
  soundEnabled = true,
  preferredCameraId,
  continuousMode = false,
  onImageMatch,
  onImageMatchAddAsNew,
  locationId,
}: CameraBarcodeScannerProps) {
  // ── Image-match overlay state ────────────────────────────────────────────
  const [imageMatchOpen, setImageMatchOpen] = useState(false);
  const [imageMatchLoading, setImageMatchLoading] = useState(false);
  const [imageMatchError, setImageMatchError] = useState<string | null>(null);
  const [imageMatchCandidates, setImageMatchCandidates] = useState<ImageLookupCandidate[]>([]);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [scanState, setScanState] = useState<ScanState>('initializing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [lastBarcode, setLastBarcode] = useState<string | null>(null);

  // Refs for values accessed inside html5-qrcode callback (avoids stale closure)
  const lastBarcodeRef = useRef<string | null>(null);
  const dedupeTimerRef = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const continuousModeRef = useRef(continuousMode);
  continuousModeRef.current = continuousMode;

  // ── Start scanner ─────────────────────────────────────────────────────
  const startScanner = useCallback(async () => {
    setScanState('initializing');
    setErrorMessage(null);

    // Check for camera support
    if (!navigator.mediaDevices?.getUserMedia) {
      setScanState('error');
      setErrorMessage('Camera not supported in this browser. Use the manual barcode entry field instead.');
      return;
    }

    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      scannerRef.current = scanner;

      const config = {
        fps: 10,
        qrbox: { width: 280, height: 280 },
        aspectRatio: 1,
        disableFlip: false,
      };

      const cameraId = preferredCameraId || { facingMode: 'environment' };

      await scanner.start(
        cameraId,
        config,
        (decodedText, result) => {
          // Deduplicate rapid scans of the same barcode (use ref, not state)
          const now = Date.now();
          if (decodedText === lastBarcodeRef.current && now - dedupeTimerRef.current < DEDUP_INTERVAL_MS) {
            return;
          }
          dedupeTimerRef.current = now;
          lastBarcodeRef.current = decodedText;
          setLastBarcode(decodedText);

          const format = result?.result?.format?.formatName ?? 'UNKNOWN';

          if (soundEnabledRef.current) playBeep();
          vibrate();

          setScanState('found');
          setTimeout(() => setScanState('scanning'), 600);

          setRecentScans((prev) => [
            { barcode: decodedText, format, timestamp: now },
            ...prev.slice(0, 9),
          ]);

          onScanRef.current(decodedText, format);

          if (!continuousModeRef.current) {
            // Stop scanner after successful scan
            scanner.stop().catch(() => {});
          }
        },
        // Error callback (frame without detection — expected, not an error)
        () => {},
      );

      setScanState('scanning');

      // Check torch availability
      try {
        const track = scanner.getRunningTrackSettings();
        if (track) {
          setTorchAvailable(true);
        }
      } catch {
        setTorchAvailable(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setScanState('error');

      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setErrorMessage('Camera permission denied. Allow camera access in your browser settings, or use the manual barcode entry field.');
      } else if (msg.includes('NotFoundError') || msg.includes('no camera')) {
        setErrorMessage('No camera found on this device. Use a USB barcode scanner or manual entry instead.');
      } else {
        setErrorMessage(`Camera error: ${msg}`);
      }
    }
  }, [preferredCameraId]);

  // ── Stop scanner ──────────────────────────────────────────────────────
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // May already be stopped
      }
      scannerRef.current = null;
    }
    setScanState('initializing');
    setTorchOn(false);
    setTorchAvailable(false);
    setLastBarcode(null);
    lastBarcodeRef.current = null;
  }, []);

  // ── Image match: capture frame, lookup, show picker ──────────────────
  const startImageMatch = useCallback(async () => {
    setImageMatchOpen(true);
    setImageMatchLoading(true);
    setImageMatchError(null);
    setImageMatchCandidates([]);

    try {
      const video = document.querySelector(`#${SCANNER_ELEMENT_ID} video`) as HTMLVideoElement | null;
      const blob = await captureFrameFromVideo(video);
      const result = await productsService.lookupByImage(blob, locationId);
      setImageMatchCandidates(result.candidates);
    } catch (err) {
      setImageMatchError(err instanceof Error ? err.message : 'Image lookup failed');
    } finally {
      setImageMatchLoading(false);
    }
  }, [locationId]);

  const handleImageMatchPick = useCallback(
    (candidate: ImageLookupCandidate) => {
      setImageMatchOpen(false);
      onImageMatch?.(candidate.product);
    },
    [onImageMatch],
  );

  const handleImageMatchClose = useCallback(() => {
    setImageMatchOpen(false);
    setImageMatchError(null);
    setImageMatchCandidates([]);
  }, []);

  // "Retake photo" re-runs the capture against the live video feed. The
  // scanner stays mounted behind the picker overlay, so a fresh frame is
  // always available without stopping/restarting the MediaStream.
  const handleImageMatchRetake = useCallback(() => {
    setImageMatchError(null);
    setImageMatchCandidates([]);
    void startImageMatch();
  }, [startImageMatch]);

  // MANAGER+ escape hatch when zero candidates are returned — hand control
  // to the caller (typically: navigate to Quick-Add pre-scoped to this
  // location). The scanner closes so the route transition is unambiguous.
  const handleImageMatchAddAsNew = useCallback(() => {
    handleImageMatchClose();
    onImageMatchAddAsNew?.();
    onClose();
  }, [handleImageMatchClose, onImageMatchAddAsNew, onClose]);

  // ── Torch toggle ──────────────────────────────────────────────────────
  const toggleTorch = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      // Access the video track from the scanner's DOM element
      const video = document.querySelector(`#${SCANNER_ELEMENT_ID} video`) as HTMLVideoElement | null;
      const track = video?.srcObject instanceof MediaStream
        ? video.srcObject.getVideoTracks()[0]
        : null;
      if (track) {
        await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] });
        setTorchOn(!torchOn);
      }
    } catch {
      // Torch not supported on this track
    }
  }, [torchOn]);

  // ── Lifecycle ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      startScanner();
    } else {
      stopScanner();
    }
    return () => { stopScanner(); };
  }, [open, startScanner, stopScanner]);

  // ── Escape key to close ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const isError = scanState === 'error';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Barcode scanner"
    >
      {/* ── Close button ─────────────────────────────────────────── */}
      <div className="absolute top-4 right-4 z-10">
        <Button
          variant="ghost"
          size="md"
          onClick={onClose}
          className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border-0"
          aria-label="Close scanner"
        >
          <CloseIcon />
        </Button>
      </div>

      {/* ── Main content ─────────────────────────────────────────── */}
      <div className="flex items-center gap-6 max-w-[800px] w-full px-4">
        {/* Viewfinder area */}
        <div className="flex-1 flex flex-col items-center">
          {isError ? (
            /* Error / permission denied fallback */
            <div className="flex flex-col items-center text-center max-w-sm">
              <CameraOffIcon />
              <p className="mt-4 text-white/90 text-sm font-medium">
                {errorMessage}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                className="mt-6 bg-white/15 hover:bg-white/25 text-white border-0"
              >
                Use Manual Entry
              </Button>
            </div>
          ) : (
            <>
              {/* Viewfinder frame */}
              <div className="relative">
                {/* Scanner container — html5-qrcode renders video here */}
                <div
                  id={SCANNER_ELEMENT_ID}
                  className="w-[280px] h-[280px] md:w-[320px] md:h-[320px] lg:w-[360px] lg:h-[360px] rounded-2xl overflow-hidden"
                />

                {/* Corner brackets overlay — responsive via CSS classes */}
                <div className="absolute inset-0 pointer-events-none">
                  <ViewfinderCorners />
                </div>

                {/* Scanning line animation */}
                {scanState === 'scanning' && (
                  <div
                    className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-success-400 to-transparent opacity-80 pointer-events-none animate-scan-line"
                  />
                )}

                {/* Flash overlay */}
                {scanState === 'found' && (
                  <div className="absolute inset-0 rounded-2xl ring-4 ring-success-400 bg-success-400/10 pointer-events-none animate-pulse" />
                )}
              </div>

              {/* Status text */}
              <div className="mt-4 text-center">
                {scanState === 'initializing' && (
                  <p className="text-white/60 text-sm">Starting camera...</p>
                )}
                {scanState === 'scanning' && (
                  <p className="text-white/70 text-sm">
                    Point camera at barcode
                  </p>
                )}
                {scanState === 'found' && lastBarcode && (
                  <p className="text-success-400 text-sm font-mono tabular-nums font-medium">
                    {lastBarcode}
                  </p>
                )}
              </div>

              {/* Torch + Match-by-image controls */}
              <div className="mt-4 flex items-center gap-2">
                {torchAvailable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleTorch}
                    className={
                      torchOn
                        ? 'bg-warning-500/30 text-warning-300 border-0'
                        : 'bg-white/10 hover:bg-white/20 text-white/70 border-0'
                    }
                    aria-label={torchOn ? 'Turn off flashlight' : 'Turn on flashlight'}
                  >
                    <FlashlightIcon on={torchOn} />
                    <span className="ml-2">{torchOn ? 'Torch On' : 'Torch'}</span>
                  </Button>
                )}
                {onImageMatch && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startImageMatch}
                    disabled={imageMatchLoading}
                    className="bg-white/10 hover:bg-white/20 text-white/70 border-0"
                    aria-label="Match by image"
                  >
                    <CameraSparkleIcon />
                    <span className="ml-2">Match by image</span>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Image-match candidate picker overlay */}
        <ImageMatchCandidatePicker
          open={imageMatchOpen}
          isLoading={imageMatchLoading}
          error={imageMatchError}
          candidates={imageMatchCandidates}
          onPick={handleImageMatchPick}
          onRetake={handleImageMatchRetake}
          onAddAsNew={onImageMatchAddAsNew ? handleImageMatchAddAsNew : undefined}
          onClose={handleImageMatchClose}
        />

        {/* Recent scans sidebar (lg+ only) */}
        {recentScans.length > 0 && (
          <div className="hidden lg:block w-[200px] shrink-0">
            <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
              Recent Scans
            </h3>
            <div className="space-y-1.5 max-h-[360px] overflow-y-auto">
              {recentScans.map((scan, idx) => (
                <div
                  key={`${scan.barcode}-${scan.timestamp}`}
                  className={
                    idx === 0
                      ? 'px-3 py-2 rounded-lg text-xs bg-success-500/20 text-success-300'
                      : 'px-3 py-2 rounded-lg text-xs bg-white/5 text-white/60'
                  }
                >
                  <span className="font-mono tabular-nums block">{scan.barcode}</span>
                  <span className="text-[10px] opacity-60">{scan.format}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
